import { AppointmentBranch, GroupedBranch } from '@/type/Appointment'
import { getBranchDisplayNamei18n } from '@/utils/branch'
import { getDistance, orderByDistance } from 'geolib'
import { useTranslation } from 'i18next-vue'
import { ref } from 'vue'

export interface Doctor {
  uuid: string | null
  nickname: string
  text: string
  picture: string
}

export function useAppointmentSelections() {
  const { t } = useTranslation()

  const BRANCH_GROUP_KEYS: Record<string, string> = {
    กรุงเทพและปริมณฑล: 'appointmentrequest.branch_group.bangkok',
    จังหวัดอื่น: 'appointmentrequest.branch_group.other',
  }
  const translateBranchGroup = (name: string) => (BRANCH_GROUP_KEYS[name] ? t(BRANCH_GROUP_KEYS[name]) : name)

  // Branch state
  const branchText = ref('')
  const showBranchPicker = ref(false)

  // Doctor state
  const doctorText = ref('')
  const selectedDoctor = ref<Doctor | Record<string, never>>({})
  const showDoctorPicker = ref(false)
  const doctors = ref<Doctor[]>([])
  const doctorCalendars = ref<any[]>([])
  const doctorCalendarsIn = ref<number[]>([])

  /**
   * Handle branch selection
   */
  async function onConfirmBranch(
    { text, uuid }: { text: string; uuid: string },
    callbacks: {
      clearSelect: (options: any) => void
      fetchNoAppointment: () => Promise<void>
      // fetchDoctors: (branchUuid: string) => Promise<Doctor[]>
      // onConfirmDoctor: (options: any) => void
    },
    t: (key: string) => string,
  ) {
    callbacks.clearSelect({ doctor: true, date: true, time: true })
    showBranchPicker.value = false
    branchText.value = text

    await callbacks.fetchNoAppointment()
    // const response = await callbacks.fetchDoctors(uuid)
    // doctors.value = response

    // Add "no specific doctor" option at the beginning
    // doctors.value.unshift({
    //   uuid: null,
    //   nickname: '',
    //   text: t('appointmentrequest.note_select_doctor'),
    //   picture: '',
    // })

    // Auto-select "no specific doctor" as default
    // callbacks.onConfirmDoctor({
    //   selectedOptions: [
    //     {
    //       text: t('appointmentrequest.note_select_doctor'),
    //       uuid: null,
    //       picture: '',
    //       nickname: '',
    //     },
    //   ],
    // })
  }

  /**
   * Handle doctor selection
   */
  async function onConfirmDoctor(
    { selectedOptions }: { selectedOptions: Doctor[] },
    callbacks: {
      clearSelect: (options: any) => void
      fetchDoctorSchedule?: (doctorUuid: string) => Promise<any>
    },
    settings: any,
  ) {
    callbacks.clearSelect({ date: true, time: true })
    showDoctorPicker.value = false
    selectedDoctor.value = selectedOptions[0]
    doctorText.value = selectedOptions[0].text

    if (settings.appoint_liff_select_doctor_without_workhours) {
      return
    } else if (selectedOptions[0].uuid !== null && selectedOptions[0].uuid !== '0' && callbacks.fetchDoctorSchedule) {
      const response = await callbacks.fetchDoctorSchedule(selectedOptions[0].uuid)
      doctorCalendars.value = response
      doctorCalendarsIn.value = response.map((x: { start: string | number | Date }) => {
        return new Date(x.start).setHours(0, 0, 0, 0)
      })
    }
  }

  /**
   * Clear doctor selection
   */
  function clearDoctorSelection() {
    showDoctorPicker.value = false
    selectedDoctor.value = {}
    doctorText.value = ''
    doctorCalendars.value = []
    doctorCalendarsIn.value = []
  }

  /**
   * Clear branch selection
   */
  function clearBranchSelection() {
    showBranchPicker.value = false
    branchText.value = ''
  }

  function updateBranchTextByLanguage(branches: AppointmentBranch[], branchUuid: string | null, language: string) {
    if (!branchUuid) return
    const branch = branches.find(b => b.uuid === branchUuid)
    if (branch) {
      branchText.value = getBranchDisplayNamei18n(branch, language)
    }
  }

  function formatBranchGroup(branchList: AppointmentBranch[]): GroupedBranch[] {
    const groupMap = new Map<number, { name: string; order: number; branches: AppointmentBranch[] }>()

    for (const branch of branchList) {
      for (const membership of branch.groups) {
        if (!groupMap.has(membership.id)) {
          groupMap.set(membership.id, { name: membership.name, order: membership.group_order, branches: [] })
        }
        groupMap.get(membership.id)!.branches.push({ ...branch, display_order: membership.branch_order })
      }
    }

    const grouped = Array.from(groupMap.values())
      .sort((a, b) => a.order - b.order)
      .map(({ name, branches }) => ({
        name: translateBranchGroup(name),
        branches: [...branches].sort((a, b) => a.display_order - b.display_order),
      }))

    // เพิ่มสาขาที่ไม่มีอยู่ในกลุ่มใดเลย ลงในกลุ่มอื่นๆ
    const others = branchList
      .filter(branch => branch.groups.length === 0)
      .sort((a, b) => a.display_order - b.display_order)

    if (others.length > 0) {
      grouped.push({ name: t('appointmentrequest.other_branch'), branches: others })
    }

    return grouped
  }

  /**
   * Group branches into regular groups (preserving backend order) and prepend
   * a "สาขาใกล้ฉัน" group containing the nearest branch + all branches within 10KM.
   * Branches without coordinates are excluded from the recommended group.
   */
  function formatBranchGroupWithRecommended(
    branchList: AppointmentBranch[],
    userCoords: { latitude: number; longitude: number },
    recommendationRadius = 10_000, // 10 KM
  ): GroupedBranch[] {
    const withCoords = branchList.filter(b => b.map_latitude !== null && b.map_longitude !== null)

    const geoPoints = withCoords.map(b => ({
      ...b,
      latitude: b.map_latitude as number,
      longitude: b.map_longitude as number,
    }))

    const sorted = orderByDistance(userCoords, geoPoints) as typeof geoPoints

    const recommended: AppointmentBranch[] = []
    for (let i = 0; i < sorted.length; i++) {
      const dist = getDistance(userCoords, { latitude: sorted[i].latitude, longitude: sorted[i].longitude })
      if (i === 0) {
        recommended.push(sorted[i])
      } else if (dist <= recommendationRadius) {
        recommended.push(sorted[i])
      } else {
        break
      }
    }

    return [{ name: t('appointmentrequest.nearby_branch'), branches: recommended }, ...formatBranchGroup(branchList)]
  }

  return {
    // Branch
    branchText,
    showBranchPicker,
    // Doctor
    doctorText,
    selectedDoctor,
    showDoctorPicker,
    doctors,
    doctorCalendars,
    doctorCalendarsIn,
    // Handlers
    onConfirmBranch,
    onConfirmDoctor,
    clearDoctorSelection,
    clearBranchSelection,
    updateBranchTextByLanguage,
    // Helpers
    formatBranchGroup,
    formatBranchGroupWithRecommended,
  }
}
